import SvgIcon from '@home/components/SvgIcon';
import Gpt, { getVideoPagePromptList } from '@op/components/GPT';
import { sentimentIconMap } from '@op/constants';
import { useFrontWebConfig } from '@op/hooks/useFrontWebConfig';
import React, { useEffect, useMemo, useState } from 'react';
import './AiContentSummary.scss';
import { SentimentType } from '@op/types/common';
import StatusTips from '@op/components/GPT/components/StatusTips';
import { 
  GPTErrorStatusType,
  GPTType,
  LoadingTipsTextType,
  StatusType,
} from '@op/components/GPT/types';
import useGeySummary from '@op/components/GPT/hooks/useGetSummary';
import usePromptModal from '@op/components/GPT/hooks/usePromptModal';
import useLanguage from '@op/hooks/useLanguage';
import { DEFAULT_MODAL } from '@op/components/GPT/constants';

export const loadingTipsText: LoadingTipsTextType = {
  title: {
    gpt: 'Generating summary by GPT4.0',
    deepseek: 'Generating summary by DeepSeek',
    gemini: 'Generating summary by Gemini',
  },
  subTitle: '(Expected to wait for 0.5h ~ 4h)',
  totalTitle: undefined,
  queen: {
    gpt: [
      'After the AI summary is complete, it will be displayed automatically. No need to wait on this page.',
    ],
    deepseek: [
      'After the AI summary is complete, it will be displayed automatically. No need to wait on this page.',
    ],
    gemini: [
      'After the AI summary is complete, it will be displayed automatically. No need to wait on this page.',
    ],
    advanced: [
      'After the AI summary is complete, it will be displayed automatically. No need to wait on this page.',
    ],
  },
};

const failedTipsTextFunc = (title = 'subtitle') => {
  return {
    min: {
      nodate: `Can't generate AI summary: No ${title} found`,
      failed:
        'Unable to generate content summary due to source website restrictions or API instability, Please {btn}',
      urlerror: 'Unable to recognize the link',
      nodatasuccess:
        'Content scraped successfully, but no valid summary could be generated',
    },
  };
};

export default function AiContentSummary({
  data,
  gameInfo,
  gptSummary,
  mediaType,
}: {
  data: any;
  gameInfo: any;
  gptSummary: any;
  mediaType?: 'comment' | 'subtitle' | 'danmaku';
}) {
  const channelList = useFrontWebConfig({
    keys:
      mediaType === 'subtitle'
        ? 'have_video_ai_channel'
        : 'have_video_danmuku_ai_channel',
  });
  const { formatMessage, isEn } = useLanguage();

  function getSummaryText() {
    if (mediaType === 'subtitle') {
      const { summary_cn, summary_en } = gptSummary || {};
      const text = isEn ? summary_en : summary_cn;
      return text || summary_en || summary_cn;
    }
    return null;
  }

  const list = channelList.map((item: any) => item.channel);
  const text = getSummaryText();

  if (!list.includes(data?.channel_name) && !text) {
    return (
      <div className="dbo-unable-summary">
        <p> {formatMessage({ id: 'Unable to generate a summary.' })}</p>
        <p>
          {formatMessage(
            {
              id: 'Because the {titile} is not available for this video.',
            },
            {
              titile: formatMessage({
                id: mediaType === 'danmaku' ? 'live stream comment' : mediaType,
              }),
            },
          )}
        </p>
      </div>
    );
  }

  return (
    <VideoGPTBox
      data={data}
      gptSummary={gptSummary}
      gameInfo={gameInfo}
      mediaType={mediaType}
    ></VideoGPTBox>
  );
}

const gptFailedMap: { [key: string]: GPTErrorStatusType } = {
  '40001': 'GPT_nodata',
  '4000': 'GPT_urlError',
  '0001': 'GPT_nodataSuccess',
};

function VideoGPTBox({
  data,
  gptSummary,
  gameInfo,
  mediaType = 'subtitle',
}: {
  data: any;
  gptSummary: any;
  gameInfo: any;
  mediaType?: 'comment' | 'subtitle' | 'danmaku';
}) {
  const [status, setStatus] = useState<StatusType>('success');
  const [errorType, setErrorType] = useState<GPTErrorStatusType>();
  const [modalType, setModalType] = useState<GPTType>(DEFAULT_MODAL);
  const [gptSummaryOptions, setGptSummaryOptions] = useState({});
  const [sentiment, setSentiment] = useState<SentimentType>();
  const { summaryRef, getSummary } = useGeySummary({
    isPreviews: false,
  });
  const { visible, toggleVisible } = usePromptModal({});
  const promptList = getVideoPagePromptList(
    mediaType === 'danmaku' ? 'video_page_danmuku' : 'video_page_prompt',
  );
  useEffect(() => {
    if (!gptSummary) return;

    const { summary_status, summary_cn, summary_en, sentiment, model_type } =
      gptSummary || {};
    let realStatus = summary_status;
    let { error_code } = gptSummary || {};
    const gptOptions: any = {};
    let errorType: GPTErrorStatusType | undefined = undefined;
    if (summary_status === 'success') {
      if (!summary_cn && !summary_en) {
        realStatus = 'failed';
        error_code = '0001';
      } else {
        gptOptions.requestFuncMap = {
          defaultRequest: () =>
            new Promise((resolve) => {
              resolve({
                report_en: summary_en,
                report_cn: summary_cn,
              });
            }),
        };
        setSentiment(sentiment);
      }
    }
    if (error_code !== '0') {
      errorType = gptFailedMap[error_code] || 'GPT_failed';
    }
    setErrorType(errorType);
    if (realStatus === 'init') {
      setStatus('loading');
    } else if (realStatus === 'success') {
      setStatus('success');
    } else {
      setStatus('failed');
    }
    setGptSummaryOptions(gptOptions);
    setModalType(DEFAULT_MODAL);
  }, [gptSummary?.summary_status]);

  const gptFilter = useMemo(() => {
    return {
      ...gameInfo,
      media_type: mediaType,
      url_and_uins: [
        {
          url: data.content_url,
          comment_uin: data.comment_uin,
          comment_time: data.comment_time,
        },
      ],
    };
  }, [data]);

  const failedTipsText = failedTipsTextFunc(
    mediaType === 'danmaku' ? 'live stream comment' : mediaType,
  );
  return (
    <>
      {['loading', 'failed'].includes(status) ? (
        <div className="dbo-gpt-card-ai-content-summary">
          <StatusTips
            loadingTipsText={loadingTipsText}
            failedTipsText={failedTipsText}
            isMultiple={false}
            isMini
            status={status}
            errorType={errorType}
            reload={() => toggleVisible()}
            promptInfo={{ modelType: modalType }}
          ></StatusTips>
          <Gpt.PromptModal
            visible={visible}
            hasAdvancedSummary={false}
            needBasicTaskId={false}
            needRequestPrompt={false}
            close={() => toggleVisible()}
            promptList={promptList}
            previewPrompt={true}
            hasResultOutput={false}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            handleConfirm={async (data) => {
              if (data.resultType === 'page') {
                setStatus('success');
                setTimeout(() => {
                  getSummary(data);
                }, 100);
              }
            }}
            filter={gptFilter}
          ></Gpt.PromptModal>
        </div>
      ) : (
        <Gpt.VideoPageCard
          className="dbo-gpt-card-ai-content-summary"
          contentCard
          filter={gptFilter}
          previewPrompt
          needRequestPrompt={false}
          hideSentiment
          hideFullSubTitle
          needBasicTaskId={false}
          ref={summaryRef}
          hasResultOutput={false}
          // linkFilterChange={false}
          title={
            sentiment ? (
              <SvgIcon
                type={sentimentIconMap[sentiment]}
                className="dbo-aigc-sentiment-icon"
              />
            ) : (
              // 这个代表没有title展示
              ' '
            )
          }
          isMini
          promptList={promptList}
          hasProgressBar={false}
          showRefresh={false}
          backGPTText={(data: any) => {
            if (data?.sentiment) {
              setSentiment(data.sentiment);
            }
          }}
          hideIcons={['save', 'refresh', 'copy']}
          hasAdvancedSummary={false}
          oriFeedsState={{ total: 30 }}
          loadingTipsText={loadingTipsText}
          failedTipsText={failedTipsText}
          hasBubble
          {...gptSummaryOptions}
        >
          {(toggleVisible: any, { total }: { total: number }) => (
            <Gpt.Button
              type="weak"
              disabled={total <= 0}
              onClick={() => toggleVisible()}
              btnText="Generate Summary"
            ></Gpt.Button>
          )}
        </Gpt.VideoPageCard>
      )}
    </>
  );
}
